
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Insight, InsightStatus } from '@/types/insight';
import { generateInsightSlug } from '@/utils/insightSlugs';

export const useInsightManagement = () => {
  const { toast } = useToast();

  const { data: insights, isLoading, refetch } = useQuery({
    queryKey: ['insights-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('insights')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Insight[];
    },
  });

  const getUniqueSlug = async (field: 'slug' | 'slug_en' | 'slug_ko', baseSlug: string, currentId?: string) => {
    const fallbackBase = baseSlug || crypto.randomUUID();
    let candidate = fallbackBase;
    let suffix = 2;

    while (suffix < 100) {
      const { data, error } = await supabase
        .from('insights')
        .select('id')
        .eq(field, candidate)
        .maybeSingle();

      if (error) throw error;
      if (!data || data.id === currentId) return candidate;

      candidate = `${fallbackBase}-${suffix}`;
      suffix += 1;
    }

    throw new Error(`Unable to generate a unique ${field}`);
  };

  const getUniqueSlugAcrossFields = async (
    fields: Array<'slug' | 'slug_en' | 'slug_ko'>,
    baseSlug: string,
    currentId?: string
  ) => {
    const fallbackBase = baseSlug || crypto.randomUUID();
    let candidate = fallbackBase;
    let suffix = 2;

    while (suffix < 100) {
      const { data, error } = await supabase
        .from('insights')
        .select('id')
        .or(fields.map((field) => `${field}.eq.${candidate}`).join(','));

      if (error) throw error;
      if (!data?.length || data.every((row) => row.id === currentId)) return candidate;

      candidate = `${fallbackBase}-${suffix}`;
      suffix += 1;
    }

    throw new Error(`Unable to generate a unique slug across ${fields.join(', ')}`);
  };

  const validateMandatoryFields = (data: Partial<Insight>, status: InsightStatus) => {
    if (status === 'published') {
      if (!data.title_en && !data.title_ko) {
        throw new Error('At least one title (English or Korean) is required');
      }

      if (!data.description_en && !data.description_ko) {
        throw new Error('At least one description is required');
      }

      if (!data.meta_description_en && !data.meta_description_ko) {
        throw new Error('At least one meta description is required');
      }

      const hasEnKeywords = Array.isArray(data.meta_keywords_en) && data.meta_keywords_en.length > 0;
      const hasKoKeywords = Array.isArray(data.meta_keywords_ko) && data.meta_keywords_ko.length > 0;
      if (hasEnKeywords !== hasKoKeywords) {
        throw new Error('Meta keywords must be provided for both languages or neither');
      }

      if (!Array.isArray(data.tags) || data.tags.length === 0) {
        throw new Error('At least one tag is required');
      }

      if (!data.author) {
        throw new Error('Author is required');
      }
    }

    if (!data.title_en && !data.title_ko) {
      throw new Error('At least one title (English or Korean) is required');
    }
  };

  const handleSave = async (data: Partial<Insight>, selectedInsight: Insight | null) => {
    try {
      validateMandatoryFields(data, data.status);

      // Slugs lock once an article leaves draft (published/unpublished) so public
      // URLs and SEO stay stable. While it is still a draft we regenerate from the
      // current title, letting early typos or a later-added title self-correct.
      // The English slug is derived from title_en only — never title_ko — otherwise
      // the English-only slugifier strips all Hangul and leaves a stray digit; an
      // empty title_en falls back to a UUID inside getUniqueSlug*.
      const isDraft = !selectedInsight || selectedInsight.status === 'draft';

      const slug_en = (!isDraft && selectedInsight?.slug_en)
        ? selectedInsight.slug_en
        : await getUniqueSlugAcrossFields(
            ['slug_en', 'slug'],
            generateInsightSlug(data.title_en, false),
            selectedInsight?.id
          );
      const legacySlug = (!isDraft && selectedInsight?.slug) ? selectedInsight.slug : slug_en;
      const slug_ko = (!isDraft && selectedInsight?.slug_ko)
        ? selectedInsight.slug_ko
        : await getUniqueSlug(
            'slug_ko',
            generateInsightSlug(data.title_ko || data.title_en, true),
            selectedInsight?.id
          );

      if (selectedInsight) {
        const { error } = await supabase
          .from('insights')
          .update({
            ...data,
            slug: legacySlug,
            slug_en,
            slug_ko,
            updated_at: new Date().toISOString(),
          })
          .eq('id', selectedInsight.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('insights')
          .insert([{
            ...data,
            author: 'Admin',
            slug: legacySlug,
            slug_en,
            slug_ko
          }]);

        if (error) throw error;
      }

      await refetch();
      return true;
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save article",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleToggleVisibility = async (insight: Insight) => {
    try {
      if (insight.status !== 'published') {
        validateMandatoryFields(insight, 'published');
      }

      const { error } = await supabase
        .from('insights')
        .update({
          status: insight.status === 'published' ? 'unpublished' : 'published',
          updated_at: new Date().toISOString(),
        })
        .eq('id', insight.id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Success",
        description: `Article ${insight.status === 'published' ? 'unpublished' : 'published'} successfully`,
      });
      return true;
    } catch (error) {
      console.error('Toggle visibility error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update article status",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleDelete = async (insight: Insight) => {
    try {
      const { error } = await supabase
        .from('insights')
        .delete()
        .eq('id', insight.id);

      if (error) throw error;

      await refetch();
      toast({
        title: "Success",
        description: "Article deleted successfully",
      });
      return true;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete article",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    insights,
    isLoading,
    handleSave,
    handleToggleVisibility,
    handleDelete,
  };
};
